<template>
  <div class="cs-page">

    <!-- ── Header ── -->
    <div class="cs-header">
      <div class="cs-header-icon">
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div>
        <h2 class="cs-header-title">Chat de Suporte</h2>
        <p class="cs-header-sub">Cole o script do widget de chat e controle se ele aparece para os usuários</p>
      </div>
    </div>

    <!-- ── Status Card ── -->
    <div class="cs-status-card" :class="enabled ? 'cs-status-on' : 'cs-status-off'">
      <div class="cs-status-left">
        <div class="cs-status-dot" :class="enabled ? 'cs-dot-on' : 'cs-dot-off'"></div>
        <div>
          <div class="cs-status-label">Status do widget</div>
          <div class="cs-status-value">{{ enabled ? 'Ativo — aparecendo para todos os usuários' : 'Desativado — nenhum usuário vê o chat' }}</div>
        </div>
      </div>
      <label class="cs-toggle-wrap">
        <input type="checkbox" class="cs-toggle-input" v-model="enabled" @change="onToggle">
        <span class="cs-toggle"></span>
        <span class="cs-toggle-label">{{ enabled ? 'Desativar' : 'Ativar' }}</span>
      </label>
    </div>

    <!-- ── Script Input ── -->
    <div class="cs-card">
      <div class="cs-section-label">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><polyline points="16 18 22 12 16 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><polyline points="8 6 2 12 8 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        Script do widget
      </div>

      <div class="cs-textarea-wrap" :class="{ 'cs-textarea-valid': parsedSrc, 'cs-textarea-error': scriptRaw && !parsedSrc }">
        <textarea
          :value="resolvedScriptDisplay"
          class="cs-textarea"
          placeholder="Cole aqui o script completo do seu chat de suporte&#10;&#10;<!-- Exemplo: DeskIQ, Intercom, Crisp, Tawk.to, etc. -->"
          rows="10"
          spellcheck="false"
          @input="onScriptInput"
        ></textarea>
      </div>

      <!-- Parse feedback -->
      <div v-if="parsedSrc" class="cs-parse-ok">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
        Widget detectado: <code class="cs-code">{{ parsedSrcShort }}</code>
      </div>
      <div v-else-if="scriptRaw && !parsedSrc" class="cs-parse-warn">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        Não foi possível detectar a URL do iframe automaticamente — o widget ainda será salvo e aplicado como está.
      </div>

      <div class="cs-help-text">
        Compatível com qualquer widget que use iframe: DeskIQ, Intercom, Crisp, Tawk.to, Zendesk, entre outros.
      </div>
    </div>

    <!-- ── Params detectados ── -->
    <div v-if="parsedParams.length" class="cs-card">
      <div class="cs-section-label">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="1" fill="currentColor"/></svg>
        Parâmetros detectados
      </div>
      <div class="cs-params-grid">
        <div v-for="param in parsedParams" :key="param.key" class="cs-param" :class="{ 'cs-param-warn': param.warn }">
          <span class="cs-param-key">{{ param.key }}</span>
          <span class="cs-param-val">{{ param.val }}</span>
          <span v-if="param.warn" class="cs-param-badge-warn">⚠ não UUID</span>
        </div>
      </div>

      <!-- Aviso de tenantId inválido -->
      <div v-if="tenantIdWarning" class="cs-tenant-warn">
        <svg width="15" height="15" fill="none" viewBox="0 0 24 24" style="flex-shrink:0;margin-top:1px">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <div>
          <strong>tenantId inválido:</strong> {{ tenantIdWarning }}
          <br><small>Para encontrar seu UUID: acesse o painel DeskIQ → Configurações → Informações da conta.</small>
        </div>
      </div>
    </div>

    <!-- ── Actions ── -->
    <div class="cs-footer">
      <button class="cs-btn cs-btn-ghost" @click="clearScript" :disabled="!scriptRaw">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M19 6l-1 14H6L5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        Limpar script
      </button>
      <button class="cs-btn cs-btn-primary" @click="saveScript" :disabled="!scriptRaw">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" stroke-width="2"/><polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" stroke-width="2"/></svg>
        Salvar e aplicar
      </button>
    </div>

    <!-- ── Toast ── -->
    <transition name="cs-toast-anim">
      <div v-if="toast.show" class="cs-toast" :class="'cs-toast-' + toast.type">
        <svg v-if="toast.type === 'success'" width="16" height="16" fill="none" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
        <svg v-else width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        {{ toast.msg }}
      </div>
    </transition>

  </div>
</template>

<script>
const STORAGE_KEY_SCRIPT = 'chatSupportScript'
const STORAGE_KEY_SRC    = 'chatSupportSrc'
const STORAGE_KEY_ON     = 'chatSupportEnabled'
const CONFIG_EVENT       = 'deskiq-config-changed'  // mesmo nome usado no ChatWidget.vue

// Notifica o ChatWidget.vue (mesma aba) que a config mudou
function notifyWidget() {
  window.dispatchEvent(new CustomEvent(CONFIG_EVENT))
}

const DEFAULT_SCRIPT = `<!-- DeskIQ Chat Widget -->
<script>
(function(){
  var d=document;
  var f=d.createElement('iframe');
  f.src='https://deskiq.com.br/embed/widget?tenantId=a1b2c3d4-e5f6-7890-abcd-ef1234567890&color=%233b82f6&position=bottom-right&greeting=Precisa%20de%20ajuda%3F%20%F0%9F%92%AC&email=USER_EMAIL&clientId=CLIENT_ID&product=PRODUCT_NAME&userName=USER_NAME';
  f.allow='clipboard-write';
  f.setAttribute('allowtransparency','true');
  f.setAttribute('title','DeskIQ Chat');
  f.id='deskiq-widget';

  var s=f.style;
  function css(p,v){s.setProperty(p,v,'important')}

  function resize(open){
    css('position','fixed');
    css('bottom','0');
    css('right','0');
    css('left','auto');
    css('top','auto');
    css('border','0');
    css('background','transparent');
    css('z-index','2147483647');
    css('pointer-events','auto');
    css('max-width','100vw');
    css('max-height','100vh');
    css('transition','width .25s ease, height .25s ease');
    if(open){
      css('width','440px');
      css('height','640px');
    }else{
      css('width','96px');
      css('height','96px');
    }
  }

  window.addEventListener('message',function(e){
    if(e.data==='deskiq-widget-active') resize(true);
    if(e.data==='deskiq-widget-inactive') resize(false);
  });

  function mount(){
    if(d.getElementById('deskiq-widget')) return;
    resize(false);
    d.body.appendChild(f);
  }

  if(d.readyState==='loading'){d.addEventListener('DOMContentLoaded',mount)}
  else{mount()}
})();
<\/script>`

const DEFAULT_SRC = 'https://deskiq.com.br/embed/widget?tenantId=a1b2c3d4-e5f6-7890-abcd-ef1234567890&color=%233b82f6&position=bottom-right&greeting=Precisa%20de%20ajuda%3F%20%F0%9F%92%AC&email=USER_EMAIL&clientId=CLIENT_ID&product=PRODUCT_NAME&userName=USER_NAME'

export default {
  name: 'ChatSuporte',

  data() {
    // Verifica se a URL salva ainda é o placeholder fictício (não uma URL real configurada)
    // IMPORTANTE: só olhamos a URL salva (STORAGE_KEY_SRC), nunca o texto do script —
    // porque o DEFAULT_SCRIPT sempre contém o placeholder no corpo do template.
    const PLACEHOLDER = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
    const savedSrc    = localStorage.getItem(STORAGE_KEY_SRC) || ''
    const savedScript = localStorage.getItem(STORAGE_KEY_SCRIPT) || ''
    const srcIsPlaceholder = savedSrc.includes(PLACEHOLDER)

    // Se a URL salva ainda for o placeholder, limpa só a URL e o script
    // (preserva o STORAGE_KEY_ON para não perder o toggle ativo)
    if (srcIsPlaceholder) {
      localStorage.removeItem(STORAGE_KEY_SRC)
      localStorage.removeItem(STORAGE_KEY_SCRIPT)
    }

    // O enabled é lido SEMPRE do localStorage — nunca é zerado por causa do script
    const enabled = localStorage.getItem(STORAGE_KEY_ON) === 'true'

    return {
      scriptRaw: srcIsPlaceholder ? DEFAULT_SCRIPT : (savedScript || DEFAULT_SCRIPT),
      parsedSrc: srcIsPlaceholder ? ''             : savedSrc,
      scriptMode: localStorage.getItem('chatSupportMode') || 'iframe',
      loaderDataAttrs: JSON.parse(localStorage.getItem('chatSupportLoaderAttrs') || '{}'),
      enabled,
      toast: { show: false, type: 'success', msg: '' },
    }
  },

  computed: {
    resolvedScriptDisplay() {
      // Exibe o script com os valores reais da sessão atual (sem URL-encode — leitura humana)
      try {
        const u = JSON.parse(localStorage.getItem('userData') || '{}')
        const email = u.user_email || u.email || ''
        const id    = String(u.id || '')
        const name  = u.user_name || u.name || ''
        return this.scriptRaw
          // Placeholders internos do sistema
          .replace(/USER_EMAIL/g,   email)
          .replace(/CLIENT_ID/g,    id)
          .replace(/PRODUCT_NAME/g, 'VSLBOOSTER')
          .replace(/USER_NAME/g,    name)
          // Placeholders {{...}} nos parâmetros da URL (colados pelo usuário)
          .replace(/([&?]email=)\{\{[^}]*\}\}/gi,      '$1' + email)
          .replace(/([&?]clientId=)\{\{[^}]*\}\}/gi,    '$1' + id)
          .replace(/([&?]userName=)\{\{[^}]*\}\}/gi,    '$1' + name)
          // Versões URL-encoded de {{ e }} (%7B%7B e %7D%7D)
          .replace(/([&?]email=)%7B%7B[^%]*%7D%7D/gi,   '$1' + email)
          .replace(/([&?]clientId=)%7B%7B[^%]*%7D%7D/gi, '$1' + id)
          .replace(/([&?]userName=)%7B%7B[^%]*%7D%7D/gi, '$1' + name)
      } catch {
        return this.scriptRaw
      }
    },
    parsedSrcShort() {
      try {
        const url = new URL(this.parsedSrc)
        return url.hostname + url.pathname.slice(0, 30) + (url.pathname.length > 30 ? '…' : '')
      } catch {
        return this.parsedSrc.slice(0, 50) + '…'
      }
    },
    parsedParams() {
      try {
        const url = new URL(this.parsedSrc)
        // Resolve os placeholders dinâmicos com os dados reais do usuário logado
        let u = {}
        try { u = JSON.parse(localStorage.getItem('userData') || '{}') } catch { /**/ }
        const email = u.user_email || u.email || '(não logado)'
        const id    = String(u.id || '(não logado)')
        const name  = u.user_name || u.name || '(não logado)'
        const resolved = {
          USER_EMAIL:   email,
          CLIENT_ID:    id,
          PRODUCT_NAME: 'VSLBOOSTER',
          USER_NAME:    name,
        }
        return Array.from(url.searchParams.entries()).map(([key, val]) => {
          let display = val
          // Resolve placeholders internos
          if (resolved[val] !== undefined) {
            display = resolved[val]
          }
          // Resolve {{...}} para email, clientId e userName
          const stripped = val.replace(/^\{\{|\}\}$/g, '').replace(/^%7B%7B|%7D%7D$/gi, '')
          if (stripped !== val) {
            // O valor continha {{...}}, substitui pelo dado real
            if (key === 'email')    display = email
            else if (key === 'clientId') display = id
            else if (key === 'userName') display = name
            else display = stripped  // outros params: remove apenas as chaves
          }
          return {
            key,
            val: display.length > 45 ? display.slice(0, 45) + '…' : display,
            warn: key === 'tenantId' && !this.isUUID(val),
          }
        })
      } catch {
        return []
      }
    },
    tenantIdWarning() {
      if (!this.parsedSrc) return null
      try {
        const tenantId = new URL(this.parsedSrc).searchParams.get('tenantId') || ''
        if (!tenantId) return 'tenantId não encontrado na URL.'
        if (!this.isUUID(tenantId)) {
          return `tenantId="${tenantId}" não é um UUID válido. Deve ser o UUID da sua conta DeskIQ (ex: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx), não o ID numérico do VSLBooster.`
        }
      } catch { /* ignore */ }
      return null
    },
  },

  methods: {
    isUUID(val) {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)
    },

    // Substitui os placeholders dinâmicos com os dados reais do usuário logado
    injectUserData(src) {
      try {
        const u = JSON.parse(localStorage.getItem('userData') || '{}')
        const email = encodeURIComponent(u.user_email || u.email || '')
        const id    = encodeURIComponent(u.id || '')
        const name  = encodeURIComponent(u.user_name || u.name || '')
        return src
          // Placeholders internos do sistema
          .replace(/USER_EMAIL/g,   email)
          .replace(/CLIENT_ID/g,    id)
          .replace(/PRODUCT_NAME/g, encodeURIComponent('VSLBOOSTER'))
          .replace(/USER_NAME/g,    name)
          // Placeholders {{...}} nos parâmetros da URL (colados pelo usuário)
          .replace(/([&?]email=)\{\{[^}]*\}\}/gi,      '$1' + email)
          .replace(/([&?]clientId=)\{\{[^}]*\}\}/gi,    '$1' + id)
          .replace(/([&?]userName=)\{\{[^}]*\}\}/gi,    '$1' + name)
          // Versões URL-encoded de {{ e }} (%7B%7B e %7D%7D)
          .replace(/([&?]email=)%7B%7B[^%]*%7D%7D/gi,   '$1' + email)
          .replace(/([&?]clientId=)%7B%7B[^%]*%7D%7D/gi, '$1' + id)
          .replace(/([&?]userName=)%7B%7B[^%]*%7D%7D/gi, '$1' + name)
      } catch {
        return src
      }
    },

    onScriptInput(e) {
      // Ao editar, guarda o valor digitado como template (com ou sem placeholders)
      this.scriptRaw = e.target.value
      this.parseScript()
    },

    parseScript() {
      const raw = this.scriptRaw

      // 1) Tenta formato iframe interno: f.src='...' ou iframe.src='...'
      const iframeMatch = raw.match(/iframe\.src\s*=\s*['"]([^'"]+)['"]/i)
        || raw.match(/[a-z]+\.src\s*=\s*['"]([^'"]+)['"]/i)

      if (iframeMatch) {
        this.parsedSrc = iframeMatch[1]
        this.scriptMode = 'iframe'
        return
      }

      // 2) Tenta formato loader externo: <script src="widget-loader.js" data-*>
      const loaderMatch = raw.match(/<script[^>]+src\s*=\s*["']([^"']+)["'][^>]*>/i)
      if (loaderMatch && loaderMatch[1]) {
        // Extrai todos os atributos data-* do script tag
        const dataAttrs = {}
        const attrRegex = /data-([\w-]+)\s*=\s*["']([^"']*)["']/gi
        let m
        while ((m = attrRegex.exec(raw)) !== null) {
          dataAttrs[m[1]] = m[2].replace(/\{\{|\}\}/g, '').trim()
        }
        // Salva a URL do loader + os data attrs como JSON
        this.parsedSrc = loaderMatch[1]
        this.scriptMode = 'loader'
        this.loaderDataAttrs = dataAttrs
        return
      }

      // 3) Fallback: tenta qualquer src="..."
      const fallbackMatch = raw.match(/src\s*=\s*['"]([^'"]+)['"]/i)
      this.parsedSrc = fallbackMatch ? fallbackMatch[1] : ''
      this.scriptMode = this.parsedSrc ? 'iframe' : ''
    },

    onToggle() {
      localStorage.setItem(STORAGE_KEY_ON, this.enabled ? 'true' : 'false')
      // Salva também a URL para que o ChatWidget.vue consiga montar o iframe em qualquer página
      if (this.parsedSrc) localStorage.setItem(STORAGE_KEY_SRC, this.parsedSrc)
      notifyWidget()   // avisa o ChatWidget.vue na mesma aba (storage event não funciona na mesma aba)
      this.applyWidget()
      this.showToast(this.enabled ? 'Widget ativado com sucesso' : 'Widget desativado', this.enabled ? 'success' : 'info')
    },

    async saveScript() {
      // Salva no localStorage (para aplicar na aba atual)
      localStorage.setItem(STORAGE_KEY_SCRIPT, this.scriptRaw)
      localStorage.setItem(STORAGE_KEY_SRC, this.parsedSrc)
      localStorage.setItem(STORAGE_KEY_ON, this.enabled ? 'true' : 'false')
      localStorage.setItem('chatSupportMode', this.scriptMode || 'iframe')
      localStorage.setItem('chatSupportLoaderAttrs', JSON.stringify(this.loaderDataAttrs || {}))

      // Salva no banco (persistencia real)
      try {
        const api = process.env.VUE_APP_HOST_API_BACKEND || '/api/dkbackend'
        await fetch(`${api}/chat-support-config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            script_raw: this.scriptRaw,
            parsed_src: this.parsedSrc,
            script_mode: this.scriptMode || 'iframe',
            loader_attrs: JSON.stringify(this.loaderDataAttrs || {}),
            enabled: this.enabled ? 1 : 0,
          }),
        })
      } catch (e) { console.error('[ChatSuporte] Erro ao salvar no banco:', e) }

      notifyWidget()
      this.applyWidget()
      this.showToast('Script salvo e aplicado com sucesso!', 'success')
    },

    clearScript() {
      this.scriptRaw = ''
      this.parsedSrc = ''
      localStorage.removeItem(STORAGE_KEY_SCRIPT)
      localStorage.removeItem(STORAGE_KEY_SRC)
      notifyWidget()   // avisa o ChatWidget.vue para remover o iframe
      this.removeWidget()
      this.showToast('Script removido', 'info')
    },

    applyWidget() {
      this.removeWidget()
      if (!this.enabled || !this.parsedSrc) return

      const mode = this.scriptMode || localStorage.getItem('chatSupportMode') || 'iframe'

      if (mode === 'loader') {
        // Modo loader: injeta <script src="..." data-*> diretamente
        const script = document.createElement('script')
        script.src = this.parsedSrc
        script.id = 'deskiq-widget'
        const attrs = this.loaderDataAttrs || JSON.parse(localStorage.getItem('chatSupportLoaderAttrs') || '{}')
        const userData = this.getUserData()
        Object.entries(attrs).forEach(([key, val]) => {
          // Substitui placeholders com dados do usuário
          let resolved = val
            .replace(/USER_EMAIL/gi, userData.user_email || userData.email || '')
            .replace(/CLIENT_ID/gi, String(userData.id || ''))
            .replace(/PRODUCT_NAME|PRODUCT/gi, 'VSLBOOSTER')
            .replace(/USER_NAME/gi, userData.user_name || userData.name || '')
          script.setAttribute('data-' + key, resolved)
        })
        document.body.appendChild(script)
      } else {
        // Modo iframe: cria iframe com URL
        const resolvedSrc = this.injectUserData(this.parsedSrc)
        const iframe = document.createElement('iframe')
        iframe.src = resolvedSrc
        iframe.allow = 'clipboard-write'
        iframe.setAttribute('allowtransparency', 'true')
        iframe.setAttribute('title', 'DeskIQ Chat')
        iframe.id = 'deskiq-widget'

        const css = (p, v) => iframe.style.setProperty(p, v, 'important')
        const resize = open => {
          css('position', 'fixed')
          css('bottom', '0')
          css('right', '0')
          css('left', 'auto')
          css('top', 'auto')
          css('border', '0')
          css('background', 'transparent')
          css('z-index', '2147483647')
          css('pointer-events', 'auto')
          css('max-width', '100vw')
          css('max-height', '100vh')
          css('transition', 'width .25s ease, height .25s ease')
          css('width',  open ? '440px' : '96px')
          css('height', open ? '640px' : '96px')
        }

        resize(false)

        const handler = e => {
          if (e.data === 'deskiq-widget-active')   resize(true)
          if (e.data === 'deskiq-widget-inactive')  resize(false)
        }
        window.addEventListener('message', handler)
        iframe._deskiqHandler = handler

        document.body.appendChild(iframe)
      }
    },

    getUserData() {
      try { return JSON.parse(localStorage.getItem('userData') || '{}') } catch { return {} }
    },

    removeWidget() {
      const el = document.getElementById('deskiq-widget')
      if (el) {
        if (el._deskiqHandler) window.removeEventListener('message', el._deskiqHandler)
        el.remove()
      }
    },

    showToast(msg, type = 'success') {
      this.toast = { show: true, type, msg }
      setTimeout(() => { this.toast.show = false }, 3000)
    },
  },

  async mounted() {
    // Carrega do banco primeiro, depois faz fallback pro localStorage
    try {
      const api = process.env.VUE_APP_HOST_API_BACKEND || '/api/dkbackend'
      const resp = await fetch(`${api}/chat-support-config`)
      const data = await resp.json()
      if (data && data.script_raw) {
        this.scriptRaw = data.script_raw
        this.parsedSrc = data.parsed_src || ''
        this.scriptMode = data.script_mode || 'iframe'
        this.loaderDataAttrs = JSON.parse(data.loader_attrs || '{}')
        this.enabled = !!data.enabled
        // Sincroniza localStorage
        localStorage.setItem(STORAGE_KEY_SCRIPT, this.scriptRaw)
        localStorage.setItem(STORAGE_KEY_SRC, this.parsedSrc)
        localStorage.setItem(STORAGE_KEY_ON, this.enabled ? 'true' : 'false')
        localStorage.setItem('chatSupportMode', this.scriptMode)
        localStorage.setItem('chatSupportLoaderAttrs', JSON.stringify(this.loaderDataAttrs))
      }
    } catch (e) { /* fallback para localStorage que ja foi carregado no data() */ }
    this.parseScript()
  },
}
</script>

<style scoped>
/* ── Page ── */
.cs-page {
  max-width: 760px; margin: 0 auto; padding: 24px;
  display: flex; flex-direction: column; gap: 16px;
  position: relative;
}

/* ── Header ── */
.cs-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 4px; }
.cs-header-icon {
  width: 48px; height: 48px; border-radius: 14px; flex-shrink: 0;
  background: linear-gradient(135deg, #0D3349, #1a4d6b);
  color: #fff; display: flex; align-items: center; justify-content: center;
}
.cs-header-title { font-size: 20px; font-weight: 800; color: var(--dk-text-primary, #0D3349); margin: 0 0 4px; }
.cs-header-sub { font-size: 13.5px; color: var(--dk-text-muted, #8a94a6); margin: 0; }

/* ── Status card ── */
.cs-status-card {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-radius: 14px; border: 1.5px solid;
  flex-wrap: wrap; gap: 12px;
}
.cs-status-on  { background: rgba(40,199,111,0.05); border-color: rgba(40,199,111,0.3); }
.cs-status-off { background: var(--dk-bg-card-hover, #fafbfc); border-color: #e5e8ef; }
.cs-status-left { display: flex; align-items: center; gap: 12px; }
.cs-status-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.cs-dot-on  { background: #28c76f; box-shadow: 0 0 0 3px rgba(40,199,111,0.2); animation: cs-pulse 2s infinite; }
.cs-dot-off { background: #aab0bc; }
@keyframes cs-pulse { 0%,100%{box-shadow:0 0 0 3px rgba(40,199,111,0.2)} 50%{box-shadow:0 0 0 6px rgba(40,199,111,0.08)} }
.cs-status-label { font-size: 11.5px; color: var(--dk-text-muted, #8a94a6); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
.cs-status-value { font-size: 13.5px; color: var(--dk-text-primary, #0D3349); font-weight: 600; margin-top: 2px; }

/* Toggle */
.cs-toggle-wrap { display: flex; align-items: center; gap: 9px; cursor: pointer; }
.cs-toggle-input { display: none; }
.cs-toggle {
  width: 44px; height: 24px; border-radius: 12px;
  background: #dde1e8; position: relative; transition: background 0.2s; flex-shrink: 0;
}
.cs-toggle-input:checked + .cs-toggle { background: #28c76f; }
.cs-toggle::after {
  content: ''; position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%; background: var(--dk-bg-card, #fff);
  transition: transform 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.15);
}
.cs-toggle-input:checked + .cs-toggle::after { transform: translateX(20px); }
.cs-toggle-label { font-size: 13.5px; font-weight: 600; color: var(--dk-text-primary, #344563); }

/* ── Card ── */
.cs-card {
  background: var(--dk-bg-card, #fff); border: 1.5px solid #e5e8ef; border-radius: 14px; padding: 20px;
  display: flex; flex-direction: column; gap: 12px;
}
.cs-section-label {
  display: flex; align-items: center; gap: 7px;
  font-size: 11.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.07em; color: var(--dk-text-faint, #aab0bc);
}

/* Textarea */
.cs-textarea-wrap {
  border: 1.5px solid var(--dk-border, #e5e8ef); border-radius: 10px; overflow: hidden;
  transition: border-color 0.15s;
}
.cs-textarea-wrap:focus-within { border-color: #E86A10; box-shadow: 0 0 0 3px rgba(232,106,16,0.1); }
.cs-textarea-valid { border-color: #28c76f !important; }
.cs-textarea-error { border-color: #E86A10 !important; }
.cs-textarea {
  width: 100%; padding: 14px; border: none; outline: none; resize: vertical;
  font-family: 'Courier New', Courier, monospace; font-size: 12.5px;
  color: #1a2d42; background: var(--dk-bg-card-hover, #fafbfc); line-height: 1.6;
  min-height: 160px;
}

/* Parse feedback */
.cs-parse-ok {
  display: flex; align-items: center; gap: 7px;
  font-size: 12.5px; color: #1e9957; font-weight: 500;
}
.cs-parse-warn {
  display: flex; align-items: flex-start; gap: 7px;
  font-size: 12.5px; color: #E86A10; font-weight: 500; line-height: 1.4;
}
.cs-code {
  background: rgba(40,199,111,0.1); color: #1e9957;
  padding: 2px 7px; border-radius: 5px; font-size: 11.5px;
  font-family: 'Courier New', Courier, monospace;
}
.cs-help-text { font-size: 12px; color: var(--dk-text-faint, #aab0bc); }

/* Params */
.cs-params-grid { display: flex; flex-direction: column; gap: 6px; }
.cs-param { display: flex; gap: 10px; padding: 8px 12px; background: var(--dk-bg-card-hover, #fafbfc); border-radius: 8px; align-items: center; }
.cs-param-warn { background: rgba(232,106,16,0.06); border: 1px solid rgba(232,106,16,0.25); }
.cs-param-key { font-size: 12px; font-weight: 700; color: var(--dk-text-primary, #0D3349); min-width: 120px; }
.cs-param-val { font-size: 12px; color: var(--dk-text-secondary, #6b7280); word-break: break-all; flex: 1; }
.cs-param-badge-warn { font-size: 11px; font-weight: 700; color: #E86A10; background: rgba(232,106,16,0.1); padding: 2px 8px; border-radius: 20px; white-space: nowrap; }
.cs-tenant-warn {
  display: flex; gap: 10px; align-items: flex-start;
  padding: 12px 14px; border-radius: 10px; margin-top: 4px;
  background: rgba(193,43,43,0.06); border: 1px solid rgba(193,43,43,0.25);
  color: #C12B2B; font-size: 12.5px; line-height: 1.5;
}
.cs-tenant-warn strong { font-weight: 700; }
.cs-tenant-warn small { color: var(--dk-text-muted, #8a94a6); font-size: 11.5px; }

/* ── Footer ── */
.cs-footer { display: flex; justify-content: flex-end; gap: 10px; }
.cs-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 20px; border-radius: 10px; border: none;
  font-size: 13.5px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.cs-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.cs-btn-primary { background: linear-gradient(135deg, #E86A10, #f08535); color: #fff; box-shadow: 0 4px 12px rgba(232,106,16,0.25); }
.cs-btn-primary:hover:not(:disabled) { background: linear-gradient(135deg, #cf5e0d, #E86A10); }
.cs-btn-ghost { background: var(--dk-bg-inset, #f5f6fa); color: var(--dk-text-secondary, #6b7280); }
.cs-btn-ghost:hover:not(:disabled) { background: #edf0f3; }

/* ── Toast ── */
.cs-toast {
  position: fixed; bottom: 24px; right: 24px; z-index: 9999;
  display: flex; align-items: center; gap: 10px;
  padding: 12px 18px; border-radius: 12px;
  font-size: 13.5px; font-weight: 600;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
}
.cs-toast-success { background: #0D3349; color: #fff; }
.cs-toast-info    { background: #344563; color: #fff; }
.cs-toast-error   { background: #C12B2B; color: #fff; }
.cs-toast-anim-enter-active, .cs-toast-anim-leave-active { transition: all 0.25s; }
.cs-toast-anim-enter, .cs-toast-anim-leave-to { opacity: 0; transform: translateY(12px); }
</style>
